import React, { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Trans } from '@lingui/react'

import useUserOnboardingForm from './useUserOnboardingForm'
import ModalWelcome from './UserOnboardingModalWelcome'
import useFetchUserOnboardingData from './useFetchUserOnboardingData'
import UserOnboardingUpdateProfile from './UserOnboardingUpdateProfile'

import ModalLinkO365 from '../admin-onboarding/AdminOnboardingModalLinkO365'
import ModalGetStarted from '../admin-onboarding/AdminOnboardingModalGetStarted'

import PageNotFound from '../../../layout/PageNotFound'
import PageWithState from '../../../layout/PageWithState'
import SkeletonPage from '../../../layout/SkeletonPage'
import useNotifications from '../../../hooks/useNotifications'
import useAppState from '../../../hooks/useAppState'
import useAppSecrets from '../../../hooks/useAppSecrets'
import useTracking from '../../../hooks/useTracking'
import useAppCultures from '../../../hooks/useAppCultures'

import updateAccountStateCompletion from '../../../../api/updateAccountStateCompletion'
import activatePartner from '../../../../api/activatePartner'
import completeOnboarding from '../../../../api/completeOnboarding'
import updatePartner from '../../../../api/updatePartner'
import { linkOffice365Account } from '../../../../util/office365'
import * as routes from '../../../../routes/index'

const TOTAL_STEPS = 5

const UserOnboardingModalWithState = ({ ...props }) => {
    const { step } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const { state : authorizReponse } = location
    const { refresh, currentUser } = useAppState()
    const { trackError } = useTracking()
    const { o365DrivesClientId, o365MailsAndCalendarsClientId } = useAppSecrets()
    const apolloClient = useApolloClient()
    const formState = useUserOnboardingForm(currentUser)
    const { dispatchError } = useNotifications()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const currentStep = Number(step)
    const nextStep = () => navigate(routes.userOnboarding(currentStep + 1))
    const [disclaimerChecked, checkDisclaimer] = useState(false)

    const { isFetching, genders } = useFetchUserOnboardingData()

    const { interfaceCultures } = useAppCultures()

    if (isFetching) {
        return <SkeletonPage />
    }
    switch (currentStep) {
        case 1:
            return (
                <PageWithState {...props}>
                    <ModalWelcome
                        showNextButton
                        totalSteps={TOTAL_STEPS}
                        onNext={nextStep}
                        {...props}
                    />
                </PageWithState>
            )
        case 2:
            return (
                <PageWithState {...props}>
                    <UserOnboardingUpdateProfile
                        showNextButton
                        totalSteps={TOTAL_STEPS}
                        formState={formState}
                        isLoading={isFetching}
                        genders={genders}
                        cultures={interfaceCultures}
                        onSkip={async () => {
                            await updateAccountStateCompletion(apolloClient, {
                                hasUpdatedProfile: true,
                            })
                            nextStep()
                        }}
                        onNext={async () => {
                            const isValid = formState.validate()
                            if (isValid) {
                                setIsSubmitting(true)
                                try {
                                    const input = formState.valuesToInput()
                                    await Promise.all([
                                        updatePartner(
                                            apolloClient,
                                            currentUser.id,
                                            input,
                                        ),
                                        activatePartner(
                                            apolloClient,
                                            currentUser.id,
                                        ),
                                        updateAccountStateCompletion(
                                            apolloClient,
                                            { hasUpdatedProfile: true },
                                        ),
                                    ])
                                    setIsSubmitting(false)
                                    nextStep()
                                } catch (e) {
                                    trackError(e)
                                    dispatchError({
                                        title: (
                                            <Trans id="Something went wrong!" />
                                        ),
                                        content: e.message,
                                    })
                                } finally {
                                    setIsSubmitting(false)
                                }
                            }
                        }}
                        isSubmitting={isSubmitting}
                        {...props}
                    />
                </PageWithState>
            )
        case 3: // link with mail & calendar
            return (
                <PageWithState {...props}>
                    <ModalLinkO365
                        authorizReponse={authorizReponse}
                        linkTo="mails_calendars"
                        showNextButton
                        totalSteps={TOTAL_STEPS}
                        isSubmitting={isSubmitting}
                        onNext={() => nextStep()}
                        onLinkOffice365Account={async () => {
                            setIsSubmitting(true)
                            try {
                                linkOffice365Account(
                                    o365MailsAndCalendarsClientId,
                                '',
                                '/user-onboarding',
                                'mails_calendars',
                            )
                            await updateAccountStateCompletion(apolloClient, {
                                hasLinkedO365: true,
                                hasUpdatedProfile: true,
                            })
                            } catch (e) {
                                trackError(e)
                                dispatchError({
                                    title: <Trans id="Something went wrong!" />,
                                    content: e.message,
                                })
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}
                        {...props}
                    />
                </PageWithState>
            )
        case 4: // link with drives
            return (
                <PageWithState {...props}>
                    <ModalLinkO365
                        authorizReponse={authorizReponse}
                        linkTo="drives"
                        showSkipButton
                        totalSteps={TOTAL_STEPS}
                        isSubmitting={isSubmitting}
                        onSkip={() => nextStep()}
                        onLinkOffice365Account={async () => {
                            setIsSubmitting(true)
                            try {
                                linkOffice365Account(
                                    o365DrivesClientId,
                                    '',
                                    '/user-onboarding',
                                    'drives',
                                )
                                await updateAccountStateCompletion(apolloClient, {
                                    hasLinkedO365: true,
                                    hasUpdatedProfile: true,
                                })
                            } catch (e) {
                                trackError(e)
                                dispatchError({
                                    title: <Trans id="Something went wrong!" />,
                                    content: e.message,
                                })
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}

                        {...props}
                    />
                </PageWithState>
            )
        case 5:
            return (
                <PageWithState {...props}>
                    <ModalGetStarted
                        disclaimerChecked={disclaimerChecked}
                        handleCheckDisclaimer={checkDisclaimer}
                        onGetStarted={async () => {
                            await completeOnboarding(apolloClient)
                            navigate(routes.home())
                            refresh()
                        }}
                        {...props}
                    />
                </PageWithState>
            )
        default:
            return <PageNotFound />
    }
}

export default UserOnboardingModalWithState
